import { useState, useEffect, useMemo } from 'react';
import { getUser, getUserRoles } from '../services/auth';
import { normalizeUserRole, isSuperuserRole, isAdminRole } from '../lib/user-roles';

export interface UserRolesResult {
  roles: string[];
  isLoading: boolean;
  isSuperuser: boolean;
  isAdmin: boolean;
  isTenantAdmin: boolean;
  hasRole: (role: string | string[]) => boolean;
  hasAnyRole: (roles: string[]) => boolean;
}

/** The role flags for a list of token roles; useUserRoles and code outside React share it. */
export function rolesResultFor(roles: string[], isLoading = false): UserRolesResult {
  const normalizedRoles = roles.map(normalizeUserRole);
  const isSuperuser = !isLoading && normalizedRoles.some(isSuperuserRole);
  const isAdmin = isSuperuser || normalizedRoles.some(isAdminRole);
  const isTenantAdmin = isAdmin || normalizedRoles.some((role) => ['tenant-admin', 'tenant_admin'].includes(role));
  const hasAnyRole = (rolesToCheck: string[]): boolean =>
    rolesToCheck.some((entry) => normalizedRoles.includes(normalizeUserRole(entry)));
  const hasRole = (role: string | string[]): boolean => hasAnyRole(Array.isArray(role) ? role : [role]);
  return { roles, isLoading, isSuperuser, isAdmin, isTenantAdmin, hasRole, hasAnyRole };
}

export function useUserRoles(): UserRolesResult {
  const [roles, setRoles] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    getUser()
      .then((user) => {
        if (!cancelled) {
          setRoles(getUserRoles(user));
        }
      })
      .catch((error) => {
        console.warn('[useUserRoles] Failed to load roles:', error);
        if (!cancelled) {
          setRoles([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return useMemo(() => rolesResultFor(roles, isLoading), [roles, isLoading]);
}

export default useUserRoles;
